import { DragEvent, useMemo, useRef, useState } from 'react';

import { StarFilled } from '@ant-design/icons';
import { App, Button, Card, Popconfirm, Segmented, Tag } from 'antd';
import { useTranslation } from 'react-i18next';

import DetailLeadModal from 'scenes/Leads/Components/DetailLeadModal';

import { ApiErrorData } from 'models/ApiResponse';
import { LeadCard, ProfileStatusEntry } from 'models/Lead';
import {
  AssignedPerson,
  BusyEntry,
  OperationAssignment,
  OperationAssignmentContext,
  OperationRequest,
  OperationSupervision,
  OperationTurno,
  PublishAssignmentResult,
} from 'models/OperationRequest';
import { Store } from 'models/Store';

import { LeadService } from 'services/LeadService';
import { OperationRequirementService } from 'services/OperationRequirementService';

import {
  AssignmentSlots,
  Blocker,
  RuleContext,
  StorePlan,
  activeDays,
  applyStorePlan,
  assignPerson,
  blockerFor,
  cleanForced,
  filledCount,
  leadToPerson,
  movePerson,
  peopleOf,
  personName,
  planStore,
  sanitizeSlots,
  sortTurnos,
  totalCupos,
  unassignPerson,
  weekDayDates,
} from '../data/assignment';
import { computeStartsInFromWeek } from '../data/operationCatalogs';
import {
  countFor,
  effectiveMap,
  firstName,
  storeKey,
  storeLocation,
} from '../data/supervision';
import AssignmentListView from './AssignmentListView';
import AssignmentPool from './AssignmentPool';
import AssignmentTurnoBox from './AssignmentTurnoBox';
import ForceTypeModal from './ForceTypeModal';
import PublishAssignmentModal from './PublishAssignmentModal';
import { edgeScrollDelta, usePanelsHeight } from './boardLayout';

import './AssignmentBoard.scss';

type BoardView = 'matrix' | 'list';

interface DragState {
  person: AssignedPerson;
  /** Turno de origen cuando se mueve un chip; null si viene del pool. */
  fromKey: string | null;
}

type ForceAction =
  | { kind: 'turno'; turno: OperationTurno; fromKey: string | null }
  | { kind: 'store'; storeId: number };

interface ForceState {
  person: AssignedPerson;
  conflict: BusyEntry;
  action: ForceAction;
}

interface AssignmentBoardProps {
  context: OperationAssignmentContext;
  supervision: OperationSupervision;
  onEditSupervisors: () => void;
  onSaved: (
    assignment: OperationAssignment,
    request?: OperationRequest
  ) => void;
}

function AssignmentBoard({
  context,
  supervision,
  onEditSupervisors,
  onSaved,
}: AssignmentBoardProps) {
  const { t } = useTranslation();
  const { message, modal } = App.useApp();
  const i18nBase =
    'scenes.requirements_operation.components.assign_people.board';
  const { request, stores, turnos, busy } = context;

  const [slots, setSlots] = useState<AssignmentSlots>(() =>
    sanitizeSlots(context.assignment.slots || {}, turnos)
  );
  const [forced, setForced] = useState<Record<string, string>>(
    () => context.assignment.forced || {}
  );
  const [published, setPublished] = useState(!!context.assignment.published);
  const [publishedAt, setPublishedAt] = useState(
    context.assignment.publishedAt
  );
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [view, setView] = useState<BoardView>('matrix');
  const [supView, setSupView] = useState<string>('all');
  const [dragging, setDragging] = useState<DragState | null>(null);
  const [dropStoreId, setDropStoreId] = useState<number | null>(null);
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const [forcing, setForcing] = useState<ForceState | null>(null);
  const [profileLeadId, setProfileLeadId] = useState<string | null>(null);
  const [statuses, setStatuses] = useState<ProfileStatusEntry[] | null>(null);
  const panelsRef = useRef<HTMLDivElement>(null);
  const publishRef = useRef<HTMLDivElement>(null);
  const panelsHeight = usePanelsHeight(panelsRef, publishRef);

  const eff = useMemo(
    () => effectiveMap(stores, supervision.sups, supervision.map),
    [stores, supervision]
  );
  const storesById = useMemo(
    () => new Map<number, Store>(stores.map((s) => [s.id, s])),
    [stores]
  );
  const turnosByKey = useMemo(
    () => new Map(turnos.map((tu) => [tu.key, tu])),
    [turnos]
  );
  const visibleStores = useMemo(
    () =>
      supView === 'all'
        ? stores
        : stores.filter((s) => eff[storeKey(s)] === supView),
    [stores, eff, supView]
  );
  const visibleTurnos = useMemo(() => {
    const ids = new Set(visibleStores.map((s) => s.id));
    return turnos.filter((tu) => ids.has(tu.storeId));
  }, [turnos, visibleStores]);
  const cellTurnos = useMemo(() => {
    const map = new Map<string, OperationTurno[]>();
    turnos.forEach((tu) => {
      const key = `${tu.storeId}:${tu.day}`;
      map.set(key, [...(map.get(key) || []), tu]);
    });
    map.forEach((list, key) => map.set(key, sortTurnos(list)));
    return map;
  }, [turnos]);
  const days = useMemo(() => activeDays(turnos), [turnos]);
  const dates = useMemo(() => weekDayDates(request.week), [request.week]);

  const ctx: RuleContext = { slots, busy, type: request.type, forced };
  const filled = filledCount(slots);
  const total = totalCupos(turnos);
  const pct = total ? Math.round((filled / total) * 100) : 0;
  const complete = total > 0 && filled >= total;
  const focusTurno = focusKey ? turnosByKey.get(focusKey) || null : null;
  const focusStore = focusTurno ? storesById.get(focusTurno.storeId) : null;

  const dayLabel = (day: string) => t(`${i18nBase}.days.${day}`);
  const dayShort = (day: string) => t(`${i18nBase}.days_short.${day}`);
  const turnoLabel = (turno: OperationTurno) =>
    `${storesById.get(turno.storeId)?.name || `#${turno.storeId}`} · ${dayLabel(
      turno.day
    )} ${turno.block.toUpperCase()}`;

  const commit = (next: AssignmentSlots) => {
    setSlots(next);
    setDirty(true);
    setPublished(false);
  };

  const blockerMessage = (blocker: Blocker, person: AssignedPerson) => {
    const name = personName(person);
    if (blocker.kind === 'campaign' && blocker.busy) {
      return t(`${i18nBase}.blockers.campaign`, {
        name,
        day: dayLabel(blocker.busy.day),
        brand: blocker.busy.brand,
        id: blocker.busy.requestId,
      });
    }
    if (blocker.kind === 'same-req' && blocker.otherKey) {
      const other = turnosByKey.get(blocker.otherKey);
      return t(`${i18nBase}.blockers.same_req`, {
        name,
        turno: other ? turnoLabel(other) : blocker.otherKey,
      });
    }
    return t(
      `${i18nBase}.blockers.${blocker.kind === 'dup' ? 'dup' : 'full'}`,
      {
        name,
      }
    );
  };

  const placeInTurno = (
    turno: OperationTurno,
    person: AssignedPerson,
    fromKey: string | null
  ) => {
    commit(
      fromKey
        ? movePerson(slots, fromKey, turno.key, person.leadId)
        : assignPerson(slots, turno.key, person)
    );
    if (
      focusKey === turno.key &&
      peopleOf(slots, turno.key).length + 1 >= turno.cupos
    ) {
      setFocusKey(null);
    }
  };

  const tryAssign = (
    turno: OperationTurno,
    person: AssignedPerson,
    fromKey: string | null
  ) => {
    if (fromKey === turno.key) return;
    const blocker = blockerFor(ctx, turno, person.leadId, fromKey || undefined);
    if (blocker?.soft && blocker.busy) {
      setForcing({
        person,
        conflict: blocker.busy,
        action: { kind: 'turno', turno, fromKey },
      });
      return;
    }
    if (blocker) {
      message.warning(blockerMessage(blocker, person), 3.5);
      return;
    }
    placeInTurno(turno, person, fromKey);
  };

  const reportPlan = (
    plan: StorePlan,
    person: AssignedPerson,
    store: Store
  ) => {
    const name = personName(person);
    const skipped = plan.skipped
      .map((s) =>
        t(`${i18nBase}.store_plan.skip.${s.reason.replace('-', '_')}`, {
          day: dayShort(s.day),
          brand: s.busy?.brand || '',
        })
      )
      .join(' · ');
    if (plan.added.length === 0) {
      message.warning(
        t(`${i18nBase}.store_plan.none`, { name, store: store.name, skipped }),
        4
      );
      return;
    }
    const added = plan.added
      .map((a) => `${dayShort(a.day)} ${a.block.toUpperCase()}`)
      .join(', ');
    message.success(
      skipped
        ? t(`${i18nBase}.store_plan.partial`, {
            name,
            store: store.name,
            added,
            skipped,
          })
        : t(`${i18nBase}.store_plan.all`, { name, store: store.name, added }),
      4
    );
  };

  const assignToStore = (
    storeId: number,
    person: AssignedPerson,
    force = false,
    forcedMap = forced
  ) => {
    const store = storesById.get(storeId);
    if (!store) return;
    const plan = planStore(
      { ...ctx, forced: forcedMap },
      turnos,
      storeId,
      person.leadId,
      force
    );
    if (plan.soft) {
      setForcing({
        person,
        conflict: plan.soft,
        action: { kind: 'store', storeId },
      });
      return;
    }
    if (plan.added.length) commit(applyStorePlan(slots, plan, person));
    reportPlan(plan, person, store);
  };

  const confirmForce = (reason: string) => {
    if (!forcing) return;
    const { person, action } = forcing;
    const nextForced = { ...forced, [person.leadId]: reason };
    setForced(nextForced);
    setForcing(null);
    if (action.kind === 'turno') {
      placeInTurno(action.turno, person, action.fromKey);
    } else {
      assignToStore(action.storeId, person, true, nextForced);
    }
  };

  const removeFrom = (key: string, leadId: string) =>
    commit(unassignPerson(slots, key, leadId));

  const startPoolDrag = (lead: LeadCard, e: DragEvent<HTMLDivElement>) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', lead.id);
    setDragging({ person: leadToPerson(lead), fromKey: null });
  };

  const startChipDrag = (
    person: AssignedPerson,
    fromKey: string,
    e: DragEvent<HTMLSpanElement>
  ) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', person.leadId);
    setDragging({ person, fromKey });
  };

  const endDrag = () => {
    setDragging(null);
    setDropStoreId(null);
  };

  const dropOnTurno = (turno: OperationTurno) => {
    if (dragging) tryAssign(turno, dragging.person, dragging.fromKey);
    endDrag();
  };

  const dropOnStore = (storeId: number) => {
    if (dragging && !dragging.fromKey) {
      assignToStore(storeId, dragging.person);
    }
    endDrag();
  };

  const openProfile = async (leadId: string) => {
    if (!statuses) {
      const response = await LeadService.getProfileStatuses();
      if (response.success) {
        setStatuses((response.data as ProfileStatusEntry[]) || []);
      } else {
        const error = response.data as ApiErrorData;
        message.error(
          (error.message as string) || t(`${i18nBase}.errors.statuses`),
          3.5
        );
        return;
      }
    }
    setProfileLeadId(leadId);
  };

  const save = async () => {
    setSaving(true);
    const cleanedForced = cleanForced(slots, forced);
    const response = await OperationRequirementService.saveAssignment(
      request.id,
      { slots, forced: cleanedForced, publish: false }
    );
    setSaving(false);
    if (response.success) {
      const now = new Date().toISOString();
      setForced(cleanedForced);
      setDirty(false);
      setPublished(false);
      setPublishedAt(null);
      onSaved(
        {
          slots,
          forced: cleanedForced,
          published: false,
          publishedAt: null,
          updatedAt: now,
          notification: context.assignment.notification ?? null,
        },
        response.data as OperationRequest
      );
      message.success(t(`${i18nBase}.messages.saved`, { id: request.id }), 3.5);
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) || t(`${i18nBase}.errors.save`),
        3.5
      );
    }
  };

  const handlePublished = ({
    request: updated,
    notification,
  }: PublishAssignmentResult) => {
    const cleanedForced = cleanForced(slots, forced);
    const publishedOn = new Date().toISOString();
    setPublishOpen(false);
    setForced(cleanedForced);
    setDirty(false);
    setPublished(true);
    setPublishedAt(publishedOn);
    onSaved(
      {
        slots,
        forced: cleanedForced,
        published: true,
        publishedAt: publishedOn,
        updatedAt: publishedOn,
        notification,
      },
      updated
    );

    if (!notification) {
      message.success(
        t(`${i18nBase}.messages.published_locked`, { id: request.id }),
        3.5
      );
      return;
    }
    if (!notification.failed.length) {
      message.success(
        t(`${i18nBase}.messages.published_notified`, {
          id: request.id,
          count: notification.sent,
        }),
        3.5
      );
      return;
    }
    modal.warning({
      title: t(`${i18nBase}.notify_result.title`, {
        sent: notification.sent,
        failed: notification.failed.length,
      }),
      width: 520,
      content: (
        <ul className="assignment-board__notify-failed">
          {notification.failed.map((f) => (
            <li key={f.leadId}>
              <strong>{f.name || f.leadId}</strong>
              {` — ${f.email || t(`${i18nBase}.notify_result.no_email`)}: ${t(
                `${i18nBase}.notify_result.reasons.${f.reason}`
              )}`}
            </li>
          ))}
        </ul>
      ),
      okText: t(`${i18nBase}.notify_result.ok`),
    });
  };

  const handleEditSupervisors = () => {
    if (!dirty) {
      onEditSupervisors();
      return;
    }
    modal.confirm({
      title: t(`${i18nBase}.edit_supervisors_confirm.title`),
      content: t(`${i18nBase}.edit_supervisors_confirm.content`),
      okText: t(`${i18nBase}.edit_supervisors_confirm.ok`),
      cancelText: t(`${i18nBase}.edit_supervisors_confirm.cancel`),
      onOk: onEditSupervisors,
    });
  };

  const startsIn = computeStartsInFromWeek(request.week, request.startDate);
  const startLabel = () => {
    if (startsIn === 0)
      return t('scenes.requirements_operation.scene.start.today');
    if (startsIn === 1) {
      return t('scenes.requirements_operation.scene.start.tomorrow');
    }
    return t('scenes.requirements_operation.scene.start.in_days', {
      days: startsIn,
    });
  };

  const progressColor = complete
    ? '#1b8a4b'
    : pct >= 50
    ? '#d98a00'
    : '#143489';

  const renderStoreCell = (store: Store) => {
    const sup = eff[storeKey(store)];
    const canDrop = !!dragging && !dragging.fromKey;
    return (
      <td
        className={`assignment-board__store-cell${
          dropStoreId === store.id ? ' assignment-board__store-cell--drop' : ''
        }`}
        onDragOver={(e) => {
          if (!canDrop) return;
          e.preventDefault();
          if (dropStoreId !== store.id) setDropStoreId(store.id);
        }}
        onDragLeave={() => {
          if (dropStoreId === store.id) setDropStoreId(null);
        }}
        onDrop={(e) => {
          e.preventDefault();
          dropOnStore(store.id);
        }}
      >
        <div className="assignment-board__store-name">
          {store.isVip && <StarFilled className="assignment-board__star" />}
          <span>{store.name}</span>
        </div>
        <div className="assignment-board__store-loc">
          {`${store.chain} · ${storeLocation(store)}`}
        </div>
        {sup && (
          <div className="assignment-board__store-sup">
            {t(`${i18nBase}.store_sup`, { name: firstName(sup) })}
          </div>
        )}
        {canDrop && (
          <div className="assignment-board__store-drop-hint">
            {t(`${i18nBase}.store_drop_hint`)}
          </div>
        )}
      </td>
    );
  };

  const renderMatrix = () => (
    <div className="assignment-board__matrix" onDragOver={autoScrollPanel}>
      <table className="assignment-board__table">
        <thead>
          <tr>
            <th className="assignment-board__th-store">
              {t(`${i18nBase}.store_col`)}
            </th>
            {days.map((d) => (
              <th key={d.key} className="assignment-board__th-day">
                <div className="assignment-board__day-label">
                  {dayLabel(d.key)}
                </div>
                {dates[d.key] && (
                  <div className="assignment-board__day-date">
                    {dates[d.key]}
                  </div>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visibleStores.map((store) => (
            <tr key={store.id}>
              {renderStoreCell(store)}
              {days.map((d) => {
                const list = cellTurnos.get(`${store.id}:${d.key}`) || [];
                if (list.length === 0) {
                  return (
                    <td
                      key={d.key}
                      className="assignment-board__day-cell--empty"
                    >
                      <div className="assignment-board__no-turno">—</div>
                    </td>
                  );
                }
                return (
                  <td key={d.key}>
                    <div className="assignment-board__turnos">
                      {list.map((turno) => (
                        <AssignmentTurnoBox
                          key={turno.key}
                          turno={turno}
                          people={peopleOf(slots, turno.key)}
                          dragging={!!dragging}
                          draggingFromKey={dragging?.fromKey || null}
                          blocker={
                            dragging
                              ? blockerFor(
                                  ctx,
                                  turno,
                                  dragging.person.leadId,
                                  dragging.fromKey || undefined
                                )
                              : null
                          }
                          focus={focusKey === turno.key}
                          onDrop={() => dropOnTurno(turno)}
                          onFocus={() =>
                            setFocusKey((prev) =>
                              prev === turno.key ? null : turno.key
                            )
                          }
                          onChipDragStart={(person, e) =>
                            startChipDrag(person, turno.key, e)
                          }
                          onDragEnd={endDrag}
                          onOpenPerson={openProfile}
                          onRemove={(leadId) => removeFrom(turno.key, leadId)}
                        />
                      ))}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const autoScrollPanel = (e: DragEvent<HTMLDivElement>) => {
    const panel = e.currentTarget;
    const delta = edgeScrollDelta(e.clientY, panel.getBoundingClientRect());
    if (delta) panel.scrollTop += delta;
  };

  const profileLabel = request.profile
    ? t(`${i18nBase}.pills.profile`, { profile: request.profile })
    : null;

  return (
    <Card bordered className="assignment-board">
      <div className="assignment-board__header">
        <div className="assignment-board__header-main">
          <div className="assignment-board__header-title">
            <span className="assignment-board__req-id">{request.id}</span>
            <span className="assignment-board__req-brand">{request.brand}</span>
            <span className="assignment-board__req-campaign">
              {`· ${request.campaign}`}
            </span>
          </div>
          <div className="assignment-board__pills">
            {profileLabel && <Tag color="purple">{profileLabel}</Tag>}
            <Tag color={request.type === 'obra' ? 'orange' : 'blue'}>
              {t(
                `scenes.requirements_operation.scene.contract.${request.type}`
              )}
            </Tag>
            {request.week && <Tag>{request.week}</Tag>}
            {startsIn >= 0 && startsIn < 99 && (
              <Tag color={startsIn <= 2 ? 'red' : 'default'}>
                {startLabel()}
              </Tag>
            )}
            <span className="assignment-board__sups-text">
              {t(`${i18nBase}.sups_text`, {
                names: supervision.sups.join(' · '),
              })}
            </span>
          </div>
        </div>
        <div className="assignment-board__header-actions">
          <Button onClick={handleEditSupervisors}>
            {t(`${i18nBase}.buttons.edit_supervisors`)}
          </Button>
          <Segmented<BoardView>
            value={view}
            onChange={(value) => setView(value)}
            options={[
              { value: 'matrix', label: t(`${i18nBase}.views.matrix`) },
              { value: 'list', label: t(`${i18nBase}.views.list`) },
            ]}
          />
        </div>
      </div>

      {supervision.sups.length > 1 && (
        <div className="assignment-board__sup-filter">
          <span className="assignment-board__sup-filter-label">
            {t(`${i18nBase}.sup_filter.label`)}
          </span>
          <button
            type="button"
            className={`assignment-board__sup-chip${
              supView === 'all' ? ' assignment-board__sup-chip--on' : ''
            }`}
            onClick={() => setSupView('all')}
          >
            {t(`${i18nBase}.sup_filter.all`)}
            <span className="assignment-board__sup-chip-count">
              {stores.length}
            </span>
          </button>
          {supervision.sups.map((sup) => (
            <button
              key={sup}
              type="button"
              className={`assignment-board__sup-chip${
                supView === sup ? ' assignment-board__sup-chip--on' : ''
              }`}
              onClick={() => setSupView(sup)}
            >
              {firstName(sup)}
              <span className="assignment-board__sup-chip-count">
                {countFor(stores, eff, sup)}
              </span>
            </button>
          ))}
        </div>
      )}

      <div
        ref={panelsRef}
        className="assignment-board__body"
        style={{ height: panelsHeight }}
      >
        <AssignmentPool
          slots={slots}
          busy={busy}
          type={request.type}
          forced={forced}
          draggingLeadId={dragging?.person.leadId || null}
          focusTurno={focusTurno}
          focusLabel={focusTurno ? turnoLabel(focusTurno) : null}
          focusCommune={focusStore?.commune || null}
          dayShort={dayShort}
          onClearFocus={() => setFocusKey(null)}
          onAssignToFocus={(lead) =>
            focusTurno && tryAssign(focusTurno, leadToPerson(lead), null)
          }
          onDragStart={startPoolDrag}
          onDragEnd={endDrag}
          onOpen={openProfile}
        />
        {view === 'matrix' ? (
          renderMatrix()
        ) : (
          <div
            className="assignment-board__matrix"
            onDragOver={autoScrollPanel}
          >
            <AssignmentListView
              turnos={visibleTurnos}
              storesById={storesById}
              slots={slots}
              dragging={!!dragging}
              draggingFromKey={dragging?.fromKey || null}
              blockerFor={(turno) =>
                dragging
                  ? blockerFor(
                      ctx,
                      turno,
                      dragging.person.leadId,
                      dragging.fromKey || undefined
                    )
                  : null
              }
              dayLabel={dayLabel}
              onDrop={dropOnTurno}
              onPick={(turno) => setFocusKey(turno.key)}
              onChipDragStart={startChipDrag}
              onDragEnd={endDrag}
              onOpenPerson={openProfile}
              onRemove={removeFrom}
            />
          </div>
        )}
      </div>

      <div ref={publishRef} className="assignment-board__publish">
        <div className="assignment-board__progress">
          <div className="assignment-board__progress-head">
            <span
              className="assignment-board__progress-pct"
              style={{ color: progressColor }}
            >
              {`${pct}%`}
            </span>
            <span className="assignment-board__progress-count">
              {t(`${i18nBase}.publish.count`, { filled, total })}
            </span>
          </div>
          <div className="assignment-board__progress-track">
            <div
              className="assignment-board__progress-bar"
              style={{ width: `${pct}%`, background: progressColor }}
            />
          </div>
        </div>
        <span
          className={`assignment-board__state${
            published ? ' assignment-board__state--published' : ''
          }`}
        >
          {published
            ? t(`${i18nBase}.publish.state_published`, {
                date: publishedAt
                  ? new Date(publishedAt).toLocaleDateString()
                  : '',
              })
            : t(
                `${i18nBase}.publish.${
                  dirty ? 'state_unsaved' : 'state_proposal'
                }`
              )}
        </span>
        <div className="assignment-board__publish-actions">
          <Popconfirm
            title={t(`${i18nBase}.publish.clear_confirm`)}
            okText={t(`${i18nBase}.publish.clear_ok`)}
            cancelText={t(`${i18nBase}.publish.cancel`)}
            disabled={filled === 0}
            onConfirm={() => {
              commit({});
              setForced({});
              setFocusKey(null);
            }}
          >
            <Button danger type="text" disabled={filled === 0}>
              {t(`${i18nBase}.publish.clear`)}
            </Button>
          </Popconfirm>
          <Button loading={saving} disabled={!dirty || saving} onClick={save}>
            {t(`${i18nBase}.publish.save`)}
          </Button>
          <Button
            type="primary"
            disabled={!complete || saving || (published && !dirty)}
            title={complete ? undefined : t(`${i18nBase}.publish.incomplete`)}
            onClick={() => setPublishOpen(true)}
          >
            {t(`${i18nBase}.publish.publish`)}
          </Button>
        </div>
      </div>

      <PublishAssignmentModal
        open={publishOpen}
        request={request}
        storesCount={stores.length}
        vipStores={stores.filter((s) => s.isVip).length}
        turnosCount={turnos.length}
        filled={filled}
        total={total}
        supervisors={supervision.sups}
        slots={slots}
        forced={cleanForced(slots, forced)}
        onCancel={() => setPublishOpen(false)}
        onPublished={handlePublished}
      />

      <ForceTypeModal
        open={!!forcing}
        personLabel={forcing ? personName(forcing.person) : ''}
        conflict={forcing?.conflict || null}
        requestType={request.type}
        onCancel={() => setForcing(null)}
        onConfirm={confirmForce}
      />

      {profileLeadId && statuses && (
        <DetailLeadModal
          isOpen
          leadId={profileLeadId}
          statuses={statuses}
          handleModalCancel={() => setProfileLeadId(null)}
          onAfterUpdate={() => undefined}
        />
      )}
    </Card>
  );
}

export default AssignmentBoard;
